import { isValidPhoneNumber } from 'react-phone-number-input'
import { isValidIBAN } from 'ibantools'
import moment from 'moment'

import { checkArrayEmpty, checkArrayPresence } from 'helpers/applicationHelper'
import { differenceBetweenDates } from 'helpers/datesHelper'
import { calculateAge } from 'helpers/userHelper'
import * as validationsHelper from 'helpers/validationsHelper'
import { isValidEmailDomain } from 'helpers/validationsHelper'

export const required = (value) => (value || value === 0 ? undefined : 'Required')

export const validateNoticePeriodMonths = (value) => {
  if (value === undefined || value === null || value === '') return undefined

  const months = Number(value)
  if (!Number.isInteger(months) || months < 1 || months > 12) {
    return 'Notice Period must be between 1 and 12 months'
  }
}

export const isAlphabetOnly = (string) => {
  const alphabet = /^[a-zA-Z\s.-]+$/.test(string)
  if (string && !alphabet) {
    return 'Name can not have numbers or special characters'
  }
}

export const validateAttachment = (value) => (value && checkArrayPresence(value) ? undefined : 'Required')

export const validateEndingDate = (values) => {
  const errors = {}

  if (values.ending_date) {
    let ending_date = moment(new Date(values.ending_date), 'YYYY-MM-DD')
    if (!values.reports_to) errors.reports_to = 'Required'
    if (!values.buddy) errors.buddy = 'Required'
    if (!values.buddy) errors.buddy = 'Required'
    if (!values.manager) errors.manager = 'Required'
    if (!values.lead) errors.lead = 'Required'
    if (!values.next_reviewer) errors.next_reviewer = 'Required'

    if (differenceBetweenDates(moment(new Date(values.resignation_date), 'YYYY-MM-DD'), ending_date) < 0) {
      errors.ending_date = 'Ending Date should not be less than Resignation Date'
    }
  }
  if (values.tentative_ending_date) {
    let tentative_ending_date = moment(new Date(values.tentative_ending_date), 'YYYY-MM-DD')
    if (differenceBetweenDates(moment(new Date(values.resignation_date), 'YYYY-MM-DD'), tentative_ending_date) < 0)
      errors.tentative_ending_date = 'Tentative Ending Date should not be less than Resignation Date'
  }

  return errors
}

export const validateImage = (value) => {
  if (!Array.isArray(value)) {
    if (!value || typeof value !== 'object' || Object.keys(value).length === 0) {
      return 'Required'
    }
  } else if (Array.isArray(value) && checkArrayEmpty(value)) {
    return 'Required'
  }
  return null
}

export const validateCgpa = (value) => {
  const validFormats = /^(A|B|C|D|F|[1-5](\.\d+)?)$/i
  if (!validFormats.test(value)) {
    return 'Enter a valid CGPA (1 to 5) or grade (A to F)'
  }

  const floatValue = parseFloat(value)
  if (floatValue > 5.0) {
    return 'CGPA should not be greater than 5.0'
  }
}

export const validateMajorAndDegree = (value) => {
  if (value === 'add_new' || value === 'others') return 'Please Select Option'
}

export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  if (email?.length > 0 && !emailRegex.test(email)) {
    return 'Invalid email format'
  }
  if (email?.length > 0 && email.includes('devsinc')) {
    return "Email can't be of devsinc domain"
  }
}

export const validatePhoneNumber = (phoneNumber) => {
  if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
    return 'Invalid Phone number'
  }
}

export const validateIban = (iban, countryCode) => {
  if (iban && (!isValidIBAN(iban) || iban.substring(0, 2) !== countryCode)) {
    return 'Invalid Iban for country of residence!'
  }
}

export const validateCnic = (cnic) => {
  if (cnic && !validationsHelper.isValidCNIC(cnic)) {
    return 'Invalid CNIC number'
  }
}

export const validateUserCnic = (dob) => {
  if (calculateAge(dob) > 18) {
    return [validateCnic, required]
  } else {
    return [validateCnic]
  }
}

export const validateDependentCnic = (cnic, dob, nationality) => {
  if (calculateAge(dob) > 18) {
    if (!cnic) return `Invalid ${nationality?.identifier}`
  }
  return validateDynamicCnic(cnic, nationality)
}

export const validateDocumentNumber = (value, limit) =>
  value && limit && value.length !== limit ? `Length should be equal to limit defined: ${limit}` : undefined

export const validateDynamicCnic = (cnic, nationality) => {
  if (cnic && nationality?.identifier_regex) {
    let validCnic = new RegExp(nationality.identifier_regex)
    if (!validCnic.test(cnic)) return `Invalid ${nationality.identifier}`
  }
}

const validate = (values, props) => {
  const errors = {}
  if (values.linkedin) {
    const linkedinPattern = /^(https:\/\/)?(www\.)?linkedin\.com\/(in|profile)\/[\w-]+\/?$/i
    if (!linkedinPattern.test(values.linkedin)) {
      errors.linkedin = 'Invalid LinkedIn profile URL'
    }
  }

  if (values.id && (values.bank_account_not_opened === null || values.bank_account_not_opened === undefined))
    errors.bank_account_not_opened = 'Select bank account option'

  if (props.matchingNationality && props.matchingNationality.identifier_regex) {
    errors.cnic = validateDynamicCnic(values.cnic, props.matchingNationality)
  }
  if (values.identity_documents_attributes) {
    const identityDocumentErrors = []
    values.identity_documents_attributes?.forEach((document, i) => {
      if (!document) {
        return
      }
      const itemErrors = {}
      const setting = props.documentSettings.find(
        (item) => item.country.id === document.country && item.document_type.toLowerCase() === document.identity_type
      )
      itemErrors.document_number = validateDocumentNumber(document.document_number, setting?.character_limit)
      identityDocumentErrors[i] = itemErrors
    })
    if (identityDocumentErrors.length) {
      errors.identity_documents_attributes = identityDocumentErrors
    }
  }

  if (values.dependents) {
    const dependentItemErrors = []
    const nationalities = props.nationalities
    values.dependents?.forEach((dependentItem, i) => {
      if (!dependentItem) {
        return
      }
      const itemErrors = {}
      const dependentItemNationality = nationalities.find((item) => item.id === dependentItem.nationality_identifier_id)
      itemErrors.cnic = validateDependentCnic(dependentItem.cnic, dependentItem.dob, dependentItemNationality)
      dependentItemErrors[i] = itemErrors
    })

    if (dependentItemErrors.length) {
      errors.dependents = dependentItemErrors
    }
  }

  if (typeof props?.currentCompany != 'undefined') {
    if (values.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (values.email?.length > 0 && !emailRegex.test(values.email)) {
        errors.email = 'Invalid email'
      } else if (!isValidEmailDomain(values.email, props?.currentCompany?.email_domains)) {
        errors.email = `Invalid email address, domain must be one of: ${props?.currentCompany?.email_domains?.join(', ')}`
      }
    }
  }

  if (values.additional_info && values.additional_info.length > 250) {
    errors.additional_info = 'Characters should be less than 250'
  }
  if (checkArrayEmpty(values.cnic_back)) {
    errors.cnic_back = `${props.matchingNationality?.identifier || 'Cnic'} back image is required`
  }

  if (checkArrayEmpty(values.cnic_front)) {
    errors.cnic_front = `${props.matchingNationality?.identifier || 'Cnic'} front image is required`
  }

  if (values.probation_notes && values.probation_notes.length > 250) {
    errors.probation_notes = 'Characters should be less than 250'
  }

  if (values.about_me && values.about_me.length > 500) {
    errors.about_me = 'Characters should be less than 500'
  }

  if (values.fav_quote && values.fav_quote.length > 500) {
    errors.fav_quote = 'Characters should be less than 500'
  }

  if (values.hobbies && values.hobbies.length > 500) {
    errors.hobbies = 'Characters should be less than 500'
  }

  if (values.current_address && values.current_address.length > 500) {
    errors.current_address = 'Characters should be less than 500'
  }

  if (values.permanent_address && values.permanent_address.length > 500) {
    errors.permanent_address = 'Characters should be less than 500'
  }

  if (values.resign_reason && values.resign_reason.length > 250) {
    errors.resign_reason = 'Characters should be less than 250'
  }

  if (values.education) {
    const educationItemErrors = []
    values.education.forEach((educationItem, i) => {
      if (!educationItem) {
        return
      }
      const itemErrors = {}
      if (checkArrayEmpty(educationItem.degree_image)) {
        itemErrors.degree_image = 'Degree is required'
        educationItemErrors[i] = itemErrors
      }
    })

    if (educationItemErrors.length) {
      errors.education = educationItemErrors
    }
  }
  if (values.employment_histories) {
    const employmentItemErrors = []
    values.employment_histories.forEach((employmentItem, i) => {
      if (!employmentItem) {
        return
      }
      const itemErrors = {}

      if (i === 0 && checkArrayEmpty(employmentItem.salary_slip)) {
        itemErrors.salary_slip = 'Salary slip is required'
      }
      if (checkArrayEmpty(employmentItem.resume)) {
        itemErrors.resume = 'Resume is required'
      }
      if (Object.keys(itemErrors).length > 0) {
        employmentItemErrors[i] = itemErrors
      }
    })

    if (employmentItemErrors.length > 0) {
      errors.employment_histories = employmentItemErrors
    }
  }

  return errors
}

export default validate
